import type {
  IMapBoundingBox,
  IDisplayShop,
  IShopSelectShop,
  IMapMarkerOptions,
} from "../types";
import { SHOPS_NAMES_MAP, MAP_GEOLOCATION_PIN_ID } from "../constants";
import { getScheduleString } from "./shops";

const convertBoundingBoxToPolygon = (bb: IMapBoundingBox) => {
  const {
    leftTopLatitude,
    leftTopLongitude,
    rightBottomLatitude,
    rightBottomLongitude,
  } = bb;
  return [
    [leftTopLatitude, leftTopLongitude],
    [rightBottomLatitude, leftTopLongitude],
    [rightBottomLatitude, rightBottomLongitude],
    [leftTopLatitude, rightBottomLongitude],
  ];
};

export const pointInPolygon = (point: number[], vs: number[][]) => {
  let inside = false;
  const x = point[0],
    y = point[1];
  const start = 0;
  const end = vs.length;
  const len = end - start;
  for (let i = 0, j = len - 1; i < len; j = i++) {
    const xi = vs[i + start][0],
      yi = vs[i + start][1];
    const xj = vs[j + start][0],
      yj = vs[j + start][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const pointsInBoundingBox = (
  bb: IMapBoundingBox,
  points: number[] | Array<number[]>,
) => {
  if (!points?.length) return false;
  const poinstArr: number[][] = Array.isArray(points[0])
    ? (points as number[][])
    : [points as number[]];
  const polygon = convertBoundingBoxToPolygon(bb);
  for (let i = 0, len = poinstArr.length; i < len; i += 1) {
    const result = pointInPolygon(poinstArr[i], polygon);
    if (result === false) return false;
  }
  return true;
};

export const boundsInBoundingBox = (
  bbOuter: IMapBoundingBox,
  bbInner: IMapBoundingBox,
) => {
  const {
    leftTopLatitude,
    leftTopLongitude,
    rightBottomLatitude,
    rightBottomLongitude,
  } = bbInner;
  const points = [
    [leftTopLatitude, leftTopLongitude],
    [rightBottomLatitude, rightBottomLongitude],
  ];
  return pointsInBoundingBox(bbOuter, points);
};

export const filterShopsInBoundingBox = (
  bb: IMapBoundingBox,
  shops: IDisplayShop[] | IShopSelectShop[],
) => {
  const result: Array<IDisplayShop | IShopSelectShop> = [];
  const polygon = convertBoundingBoxToPolygon(bb);
  for (let i = 0, len = shops.length; i < len; i += 1) {
    const single = shops[i];
    const point = [single.latitude, single.longitude];
    if (pointInPolygon(point, polygon)) result.push(single);
  }
  return result as typeof shops;
};

export const fetchGeolocation = async () => {
  try {
    const _fetchGeolocation = async (provider: "browser" | "yandex") => {
      const result = await window.ymaps?.geolocation.get({ provider });
      // @ts-expect-error -- неверный тип ответа тип
      const position = result?.geoObjects.position;
      if (!position)
        throw "В ответе отсутствует объект местоположения пользователя";
      return position;
    };
    if (!window.ymaps?.geolocation)
      throw "Отсутствует модуль получения геолокации `window.ymaps.geolocation`";
    try {
      const result = await _fetchGeolocation("browser");
      return result;
    } catch (err) {
      console.warn(
        "VShopSelect -- fetchGeolocation -- ошибка при попытке использовать `provider: browser`",
        { err },
      );
      const result = await _fetchGeolocation("yandex");
      return result;
    }
  } catch (err) {
    console.error("VShopSelect -- fetchGeolocation", { err });
    return null;
  }
};

export const convertShopToMarker = (
  shop: IDisplayShop | IShopSelectShop,
  markerOptions: IMapMarkerOptions,
  extra: Record<string, unknown> = {},
) => {
  try {
    const coordinates = [shop.latitude, shop.longitude].map(
      (c: number | string) => parseFloat(String(c)),
    );
    return {
      type: markerOptions.type,
      id: shop.code,
      geometry: {
        type: markerOptions.geometryType,
        coordinates,
      },
      properties: {
        ...shop,
        ...(markerOptions.properties || {}),
        id: shop.code,
        shopTitle: SHOPS_NAMES_MAP[shop.storeType],
        actionLabel: markerOptions.actionLabel,
        schedule: getScheduleString(shop),
        ...extra,
      },
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("VSelectShop/Map/convertShopToMarker error: ", { shop, err });
    return null;
  }
};

export const convertShopToTargetMarker = (
  shop: IDisplayShop | IShopSelectShop,
  markerOptions: IMapMarkerOptions,
  extra: Record<string, unknown> = {},
) => {
  const detailedShop = {
    ...shop,
    address: shop.address
      .split(",")
      .map((s) => s.trim())
      .join(", "),
  };
  return convertShopToMarker(detailedShop, markerOptions, extra);
};

export const getGeolocationMarker = (coordinates: number[]) => ({
  type: "Feature",
  id: MAP_GEOLOCATION_PIN_ID,
  geometry: {
    type: "Point",
    coordinates,
  },
});
